import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, notification } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import apiClient from 'services/apiClient';
import { decryptRestrictedMessagesInList } from 'utils/restrictedMessageCrypto';
import { subscribeUserChat, leaveUserChat } from 'services/reverbEcho';
import { useQueryClient } from '@tanstack/react-query';
import { useMyContext } from 'Context/MyContextProvider';
import {
  appendUniqueMessage,
  applyCrossFilterSnapshots,
  applyIncomingMessageMeta,
  applyMessageStatus,
  menuLastMessageToConversationMessage,
  DEFAULT_CHAT_FILTER,
  findChatByNumber,
  getChatMenuPreviewText,
  matchesChatFilter,
  mergeChatLists,
  normalizeChatFilter,
  normalizeConversationMessage,
  resolveCrossFilterTarget,
  resolveCswFromIncomingMessage,
  resolveCswFromMenuPayload,
  shouldReplaceLastMessage,
  sortChatsByLatest,
  upsertChatInList,
} from '../chat/chatUtils';

const parseEvent = (evt) => {
  if (typeof evt !== 'string') {
    return evt;
  }
  try {
    return JSON.parse(evt);
  } catch {
    return { raw: evt };
  }
};

export const useLiveChat = () => {
  const { api, UserData, isSupportAgent } = useMyContext();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [chatAccount, setChatAccount] = useState(null);
  const broadcastUserId = chatAccount?.broadcast_user_id ?? UserData?.id;

  const [chatList, setChatList] = useState([]);
  const [messagesByContact, setMessagesByContact] = useState({});
  const [activeContact, setActiveContact] = useState(null);
  const [loadingChats, setLoadingChats] = useState(false);
  const [loadingMoreChats, setLoadingMoreChats] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [chatsPage, setChatsPage] = useState(1);
  const [hasMoreChats, setHasMoreChats] = useState(false);
  const [chatFilter, setChatFilter] = useState(DEFAULT_CHAT_FILTER);
  const [chatSearch, setChatSearch] = useState('');
  const [crossFilterNew, setCrossFilterNew] = useState({
    active: false,
    expired: false,
  });
  const [openContactCsw, setOpenContactCsw] = useState(null);

  const activeContactRef = useRef(null);
  const chatFilterRef = useRef(chatFilter);
  const chatSearchRef = useRef('');
  const chatListRef = useRef([]);
  const isSupportAgentRef = useRef(isSupportAgent);
  const lastNotifiedMsgIdRef = useRef(null);
  const abortControllerRef = useRef(null);
  const lastFetchedNumberRef = useRef(null);
  const crossFilterQueueRef = useRef([]);
  const pendingIncomingNotifyRef = useRef([]);
  const lastCrossFilterContactRef = useRef(new Map());
  const notifyIncomingRef = useRef(null);
  const fetchInitialChatsRef = useRef(null);
  const clearCrossFilterNewRef = useRef(null);
  const crossFilterSnapshotsRef = useRef(new Map());

  // Batching refs for high-frequency socket events
  const pendingChatUpdatesRef = useRef([]);
  const rafIdRef = useRef(null);

  const clearCrossFilterNew = useCallback((filterKey) => {
    const key = normalizeChatFilter(filterKey);
    setCrossFilterNew((prev) => {
      if (!prev[key]) {
        return prev;
      }
      return { ...prev, [key]: false };
    });
  }, []);

  useEffect(() => {
    clearCrossFilterNewRef.current = clearCrossFilterNew;
  }, [clearCrossFilterNew]);

  const setCrossFilterFlag = useCallback((target) => {
    setCrossFilterNew((prev) => {
      if (prev[target]) {
        return prev;
      }
      return { ...prev, [target]: true };
    });
  }, []);

  const flushSideEffectQueues = useCallback(() => {
    const crossItems = crossFilterQueueRef.current;
    const incomingItems = pendingIncomingNotifyRef.current;
    crossFilterQueueRef.current = [];
    pendingIncomingNotifyRef.current = [];

    const crossFilterContactIds = new Set();

    crossItems.forEach((item) => {
      const contactKey = String(item.chat?.number ?? '');
      if (!contactKey) {
        return;
      }

      const now = Date.now();
      const lastAt = lastCrossFilterContactRef.current.get(contactKey) || 0;
      if (now - lastAt < 4000) {
        return;
      }
      lastCrossFilterContactRef.current.set(contactKey, now);
      crossFilterContactIds.add(contactKey);

      setCrossFilterFlag(item.target);

      const label = item.target === 'active' ? 'Active' : 'Expired';
      const name = item.chat?.name || contactKey;
      const preview = getChatMenuPreviewText(item.chat) || 'New activity';
      const toastKey = `cross-filter-${item.target}-${contactKey}`;

      notification.open({
        key: toastKey,
        message: `New activity in ${label} chats`,
        description: (
          <div>
            <strong>{name}</strong>
            {': '}
            {preview}
          </div>
        ),
        btn: (
          <Button
            type="primary"
            size="small"
            onClick={() => {
              notification.destroy(toastKey);
              clearCrossFilterNewRef.current?.(item.target);
              fetchInitialChatsRef.current?.(item.target);
            }}
          >
            {`View ${label}`}
          </Button>
        ),
        placement: 'bottomRight',
        duration: 5,
      });

      try {
        const audio = new Audio('/notify.mp3');
        audio.volume = 0.25;
        audio.play();
      } catch {
        // ignore autoplay restrictions
      }
    });

    incomingItems.forEach((messageData) => {
      const contactKey = String(messageData?.number ?? '');
      if (crossFilterContactIds.has(contactKey)) {
        return;
      }
      notifyIncomingRef.current?.(messageData);
    });
  }, [setCrossFilterFlag]);

  const queueCrossFilterIfNeeded = useCallback(
    (updatedList, contactId, isOpen, messageData, shouldNotifyIncoming = false) => {
      if (isOpen) {
        return;
      }

      const chat = findChatByNumber(updatedList, contactId);
      const target = resolveCrossFilterTarget(chat, chatFilterRef.current);
      if (target) {
        if (chat) {
          crossFilterSnapshotsRef.current.set(String(contactId), chat);
        }
        crossFilterQueueRef.current.push({ target, chat, messageData });
        return;
      }

      if (
        shouldNotifyIncoming
        && messageData?.lastMessage?.from !== 'me'
      ) {
        pendingIncomingNotifyRef.current.push(messageData);
      }
    },
    []
  );

  const applyIncomingToChatList = useCallback((
    prevList,
    messageData,
    contactId,
    isOpen,
  ) => {
    if (
      isSupportAgentRef.current
      && !prevList.some((chat) => String(chat.number) === String(contactId))
    ) {
      return prevList;
    }

    const updated = upsertChatInList(prevList, messageData, { isActiveChat: isOpen });
    const chat = findChatByNumber(updated, contactId);

    if (isOpen) {
      const target = resolveCrossFilterTarget(chat, chatFilterRef.current);
      if (target) {
        const normalized = normalizeChatFilter(target);
        chatFilterRef.current = normalized;
        setChatFilter(normalized);
        clearCrossFilterNew(normalized);
        fetchInitialChatsRef.current?.(normalized);

        const switchedChat = findChatByNumber(updated, contactId);
        if (switchedChat) {
          return [switchedChat];
        }

        return updated.filter((item) => matchesChatFilter(item, normalized));
      }

      return updated.filter((item) =>
        matchesChatFilter(item, chatFilterRef.current)
      );
    }

    queueCrossFilterIfNeeded(updated, contactId, false, messageData, true);

    return updated.filter((item) =>
      matchesChatFilter(item, chatFilterRef.current)
    );
  }, [clearCrossFilterNew, queueCrossFilterIfNeeded]);

  // Batched chat list update function
  const batchedSetChatList = useCallback((updater) => {
    pendingChatUpdatesRef.current.push(updater);

    if (rafIdRef.current === null) {
      rafIdRef.current = requestAnimationFrame(() => {
        const batch = pendingChatUpdatesRef.current;
        pendingChatUpdatesRef.current = [];
        rafIdRef.current = null;

        setChatList((prev) => {
          let nextState = prev;
          for (const fn of batch) {
            nextState = fn(nextState);
          }
          return nextState;
        });

        flushSideEffectQueues();
      });
    }
  }, [flushSideEffectQueues]);

  // Cleanup RAF on unmount
  useEffect(() => {
    return () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
      }
    };
  }, []);

  useEffect(() => {
    chatFilterRef.current = chatFilter;
  }, [chatFilter]);

  useEffect(() => {
    chatListRef.current = chatList;
  }, [chatList]);

  useEffect(() => {
    isSupportAgentRef.current = isSupportAgent;
  }, [isSupportAgent]);

  useEffect(() => {
    activeContactRef.current = activeContact;
  }, [activeContact]);

  const msgList = useMemo(() => {
    if (!activeContact) {
      return [];
    }
    return messagesByContact[activeContact] || [];
  }, [activeContact, messagesByContact]);

  const setMessagesForContact = useCallback(async (contactId, messages) => {
    if (!contactId) {
      return;
    }

    const normalizedMessages = await decryptRestrictedMessagesInList(
      (messages || []).map((message) => {
        try {
          return message?.msgType === 'date'
            ? message
            : normalizeConversationMessage(message);
        } catch {
          return message;
        }
      })
    );

    setMessagesByContact((prev) => ({
      ...prev,
      [String(contactId)]: normalizedMessages,
    }));
  }, []);

  const updateMessagesForContact = useCallback((contactId, updater) => {
    if (!contactId) {
      return;
    }
    const key = String(contactId);
    setMessagesByContact((prev) => ({
      ...prev,
      [key]: updater(prev[key] || []),
    }));
  }, []);

  const fetchChats = useCallback(async ({
    page = 1,
    append = false,
    filter,
    search,
    forceFresh = false,
  } = {}) => {
    if (!UserData?.id) {
      return;
    }

    const resolvedFilter = normalizeChatFilter(filter ?? chatFilterRef.current);
    const resolvedSearch = search !== undefined
      ? String(search).trim()
      : chatSearchRef.current;

    if (search !== undefined) {
      chatSearchRef.current = resolvedSearch;
      setChatSearch(resolvedSearch);
    }

    if (page === 1) {
      setLoadingChats(true);
    } else {
      setLoadingMoreChats(true);
    }

    try {
      const queryKey = ['chats', resolvedFilter, page, resolvedSearch];
      const queryFn = async () => {
        const params = {
          page,
          per_page: 10,
          filter: resolvedFilter,
        };

        if (resolvedSearch) {
          params.search = resolvedSearch;
        }

        const res = await apiClient.get(`${api}chats`, { params });
        return res.data;
      };

      const payload = forceFresh
        ? await queryClient.fetchQuery({
          queryKey,
          queryFn,
          staleTime: 0,
        })
        : await queryClient.ensureQueryData({
          queryKey,
          queryFn,
          staleTime: 30 * 1000,
        });

      if (payload.account) {
        setChatAccount(payload.account);
      }

      const nextChats = payload.data || [];
      const snapshots = Array.from(crossFilterSnapshotsRef.current.values());

      nextChats.forEach((chat) => {
        const key = String(chat.number);
        const snap = crossFilterSnapshotsRef.current.get(key);
        if (!snap?.lastMessage || !chat.lastMessage) {
          crossFilterSnapshotsRef.current.delete(key);
          return;
        }

        if (!shouldReplaceLastMessage(snap.lastMessage, chat.lastMessage)) {
          crossFilterSnapshotsRef.current.delete(key);
        }
      });

      setChatsPage(payload.current_page || page);
      setHasMoreChats(Boolean(payload.has_more));
      setChatFilter(normalizeChatFilter(payload.filter ?? resolvedFilter));

      setChatList((prev) => {
        let nextList;

        if (append) {
          nextList = sortChatsByLatest([...prev, ...nextChats]);
        } else if (resolvedSearch) {
          nextList = sortChatsByLatest(nextChats);
        } else {
          nextList = sortChatsByLatest(mergeChatLists(prev, nextChats));
        }

        return applyCrossFilterSnapshots(nextList, snapshots, resolvedFilter);
      });
    } catch (error) {
      console.error('Error fetching chats:', error);
    } finally {
      setLoadingChats(false);
      setLoadingMoreChats(false);
    }
  }, [api, UserData?.id, queryClient]);

  const fetchInitialChats = useCallback((filter = DEFAULT_CHAT_FILTER) => {
    const normalized = normalizeChatFilter(filter);
    chatSearchRef.current = '';
    setChatSearch('');
    clearCrossFilterNew(normalized);
    return fetchChats({
      page: 1,
      append: false,
      filter: normalized,
      search: '',
      forceFresh: true,
    });
  }, [clearCrossFilterNew, fetchChats]);

  useEffect(() => {
    fetchInitialChatsRef.current = fetchInitialChats;
  }, [fetchInitialChats]);

  const searchChats = useCallback((query) => {
    const trimmed = String(query || '').trim();
    return fetchChats({ page: 1, append: false, search: trimmed });
  }, [fetchChats]);

  const fetchMoreChats = useCallback(() => {
    if (!hasMoreChats || loadingMoreChats || loadingChats) {
      return;
    }

    fetchChats({ page: chatsPage + 1, append: true });
  }, [fetchChats, hasMoreChats, loadingMoreChats, loadingChats, chatsPage]);

  const invalidateMessageQueries = useCallback((contactId) => {
    if (!contactId) {
      return;
    }

    queryClient.invalidateQueries({ queryKey: ['messages', String(contactId)] });
  }, [queryClient]);

  const fetchMessages = useCallback(
    async (contactId, handleLoader = true, page = 1, append = false, setHasMore) => {
      if (!contactId) {
        return null;
      }

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      const controller = new AbortController();
      abortControllerRef.current = controller;

      const queryKey = ['messages', contactId, page];
      const isCached = !!queryClient.getQueryData(queryKey);
      const shouldForceRefresh = page === 1 && !append;

      if (handleLoader && !isCached) {
        setLoadingMessages(true);
      }

      try {
        const fetchPage = async () => {
          const response = await apiClient.get(`${api}message/${contactId}/paginated`, {
            params: {
              page,
              per_page: 20,
            },
            signal: controller.signal,
          });
          return response.data;
        };

        const payload = shouldForceRefresh
          ? await queryClient.fetchQuery({
            queryKey,
            queryFn: fetchPage,
            staleTime: 0,
          })
          : await queryClient.ensureQueryData({
            queryKey,
            queryFn: fetchPage,
            staleTime: 60 * 1000,
          });

        const chats = payload;
        const key = String(contactId);
        const numberChanged = String(lastFetchedNumberRef.current) !== key;

        // Chat-mode (international/domestic) is only sent on page 1. Cache it so
        // paginating older messages (page 2+) never resets the chat mode.
        if (page === 1 && payload && typeof payload === 'object' && 'is_international' in payload) {
          queryClient.setQueryData(['chatMode', key], {
            is_international: Boolean(payload.is_international),
            country_iso2: payload.country_iso2 ?? null,
            flag: payload.flag ?? null,
            country_name: payload.country_name ?? null,
          });
        }

        if (setHasMore && typeof setHasMore === 'function') {
          setHasMore(chats?.has_more || false);
        }

        if (numberChanged) {
          lastFetchedNumberRef.current = key;
        }

        if (numberChanged || page === 1) {
          await setMessagesForContact(key, chats?.msg || []);
        } else if (append) {
          const decryptedPage = await decryptRestrictedMessagesInList(
            (chats?.msg || []).map((message) =>
              message?.msgType === 'date' ? message : normalizeConversationMessage(message)
            )
          );
          setMessagesByContact((prev) => {
            const prevMsgList = prev[key] || [];
            const existingIds = new Set(prevMsgList.map((msg) => msg.id));
            const uniqueNewMessages = decryptedPage.filter((msg) => !existingIds.has(msg.id));
            return {
              ...prev,
              [key]: [...uniqueNewMessages, ...prevMsgList],
            };
          });
        } else {
          const decryptedMessages = await decryptRestrictedMessagesInList(
            (chats?.msg || []).map((message) =>
              message?.msgType === 'date' ? message : normalizeConversationMessage(message)
            )
          );
          setMessagesByContact((prev) => {
            const prevMsgList = prev[key] || [];
            if (prevMsgList.length !== decryptedMessages.length) {
              return { ...prev, [key]: decryptedMessages };
            }
            const hasChanges = decryptedMessages.some((newMsg, index) => {
              const prevMsg = prevMsgList[index];
              return (
                !prevMsg ||
                prevMsg.id !== newMsg.id ||
                prevMsg.message !== newMsg.message ||
                prevMsg.created_at !== newMsg.created_at ||
                prevMsg.updated_at !== newMsg.updated_at
              );
            });
            return hasChanges ? { ...prev, [key]: decryptedMessages } : prev;
          });
        }

        return chats;
      } catch (error) {
        if (error.name === 'CanceledError' || error.name === 'AbortError') {
          return null;
        }
        console.error('Error fetching messages:', error);
        return null;
      } finally {
        if (handleLoader && !isCached) {
          setLoadingMessages(false);
        }
      }
    },
    [api, queryClient, setMessagesForContact]
  );

  const markRead = useCallback((contactId) => {
    setChatList((prev) =>
      prev.map((chat) =>
        String(chat.number) === String(contactId)
          ? { ...chat, unread: 0 }
          : chat
      )
    );
    queryClient.invalidateQueries({ queryKey: ['chats'] });
  }, [queryClient]);

  const appendOptimistic = useCallback(
    (contactId, message) => {
      updateMessagesForContact(contactId, (prev) => appendUniqueMessage(prev, message));
    },
    [updateMessagesForContact]
  );

  const isContactChatOpen = useCallback(
    (contactId) => {
      const contactKey = String(contactId);
      const chatPathMatch = location.pathname.match(/^\/chats\/(\d+)$/);
      const openedChatNumber = chatPathMatch ? chatPathMatch[1] : null;

      return (
        (openedChatNumber && openedChatNumber === contactKey) ||
        (activeContactRef.current &&
          String(activeContactRef.current) === contactKey)
      );
    },
    [location.pathname]
  );

  const publishOpenContactCsw = useCallback((contactId, cswState) => {
    if (!cswState || !isContactChatOpen(contactId)) {
      return;
    }

    setOpenContactCsw({
      contactId: String(contactId),
      is_expired: Boolean(cswState.is_expired),
      csw_expires_at: cswState.csw_expires_at ?? null,
      tick: Date.now(),
    });
  }, [isContactChatOpen]);

  const notifyIncoming = useCallback(
    (messageData) => {
      const msgid = messageData?.lastMessage?.id;
      const isNotMe = messageData?.lastMessage?.from !== 'me';
      const isChatbotTrigger =
        Boolean(messageData?.is_chatbot_trigger) ||
        Boolean(messageData?.lastMessage?.is_chatbot_trigger);

      if (isChatbotTrigger) {
        return;
      }

      if (isContactChatOpen(messageData.number)) {
        return;
      }

      const showBrowserNotification =
        isNotMe &&
        Notification.permission === 'granted' &&
        msgid !== lastNotifiedMsgIdRef.current &&
        !document.hasFocus();

      if (showBrowserNotification) {
        lastNotifiedMsgIdRef.current = msgid;
        new Notification(`You have received a new message from ${messageData?.number}`, {
          body: messageData.lastMessage.text || 'You received a new message',
        });
        const audio = new Audio('/notify.mp3');
        audio.volume = 0.3;
        audio.play();
        return;
      }

      if (isNotMe && msgid !== lastNotifiedMsgIdRef.current) {
        lastNotifiedMsgIdRef.current = msgid;
        notification.open({
          message: `New message from ${messageData?.number}`,
          description: (
            <div>
              {messageData.lastMessage.text || 'You received a new message'}
              <Button
                type="link"
                style={{ paddingLeft: 8 }}
                onClick={() => {
                  setActiveContact(String(messageData.number));
                  markRead(messageData.number);
                  navigate(`${messageData.number}`);
                  notification.destroy();
                }}
              >
                Open Chat
              </Button>
            </div>
          ),
          placement: 'bottomRight',
          duration: 3,
        });
        const audio = new Audio('/notify.mp3');
        audio.volume = 0.3;
        audio.play();
      }
    },
    [isContactChatOpen, markRead, navigate, setActiveContact]
  );

  useEffect(() => {
    notifyIncomingRef.current = notifyIncoming;
  }, [notifyIncoming]);

  const syncOpenConversationFromMenu = useCallback((messageData) => {
    const contactId = String(messageData.number);
    if (String(activeContactRef.current) !== contactId) {
      return;
    }

    const conversationMessage = menuLastMessageToConversationMessage(
      messageData.lastMessage,
      contactId
    );
    if (!conversationMessage) {
      return;
    }

    updateMessagesForContact(contactId, (prev) =>
      appendUniqueMessage(prev, conversationMessage)
    );
  }, [updateMessagesForContact]);

  const handleMenuUpdated = useCallback(
    (evt) => {
      const payload = parseEvent(evt);
      const messageData = payload?.data;
      if (!messageData?.number || !messageData?.lastMessage) {
        return;
      }

      const contactId = String(messageData.number);
      const isOpen = isContactChatOpen(contactId);

      batchedSetChatList((prev) =>
        applyIncomingToChatList(prev, messageData, contactId, isOpen)
      );
      invalidateMessageQueries(contactId);
      publishOpenContactCsw(contactId, resolveCswFromMenuPayload(messageData));
      syncOpenConversationFromMenu(messageData);
    },
    [applyIncomingToChatList, batchedSetChatList, invalidateMessageQueries, isContactChatOpen, publishOpenContactCsw, syncOpenConversationFromMenu]
  );

  const handleIncomingMessage = useCallback(
    (evt) => {
      const payload = parseEvent(evt);
      const message = payload?.message;
      if (!message) {
        return;
      }

      const contactId = String(message.wa_id || message.number || '');
      if (!contactId) {
        return;
      }

      const isChatbotTrigger = Boolean(message.is_chatbot_trigger);

      const isOpen = isContactChatOpen(contactId);

      if (!isChatbotTrigger) {
        const menuPayload = {
          number: contactId,
          name: message.profile_name || message.name,
          is_chatbot_message: Boolean(message.is_chatbot_message),
          is_chatbot_trigger: false,
          is_csw_open: message.is_csw_open,
          is_expired: message.is_expired,
          csw_expires_at: message.csw_expires_at,
          last_customer_message_at: message.last_customer_message_at,
          lastMessage: {
            text: message.text,
            from: 'opposite',
            msgType: message.msgType || message.type || 'text',
            media_id: message.media_id,
            media_url: message.media_url,
            mime_type: message.mime_type,
            file_name: message.file_name,
            latitude: message.latitude,
            longitude: message.longitude,
            location_name: message.location_name,
            location_address: message.location_address,
            id: message.id,
            time: message.created_at || message.mesgTime,
            is_chatbot_message: Boolean(message.is_chatbot_message),
            is_chatbot_trigger: false,
          },
        };

        batchedSetChatList((prev) =>
          applyIncomingToChatList(prev, menuPayload, contactId, isOpen)
        );
        invalidateMessageQueries(contactId);
        publishOpenContactCsw(contactId, resolveCswFromIncomingMessage(message));
      }

      if (isOpen) {
        updateMessagesForContact(contactId, (prev) =>
          appendUniqueMessage(prev, normalizeConversationMessage({
            ...message,
            type: message.type || message.msgType,
            msgType: message.msgType || message.type,
          }))
        );
      }
    },
    [applyIncomingToChatList, batchedSetChatList, invalidateMessageQueries, isContactChatOpen, publishOpenContactCsw, updateMessagesForContact]
  );

  const handleMessageMeta = useCallback(
    (evt) => {
      const payload = parseEvent(evt);
      const message = payload?.message;
      if (!message?.id) {
        return;
      }

      const contactId = message.wa_id ? String(message.wa_id) : '';
      if (contactId && String(activeContactRef.current) === contactId) {
        updateMessagesForContact(contactId, (prev) =>
          applyIncomingMessageMeta(prev, message)
        );
      }
    },
    [updateMessagesForContact]
  );

  const handleMessageStatus = useCallback(
    (evt) => {
      const payload = parseEvent(evt);
      const message = payload?.message;
      if (!message?.id) {
        return;
      }

      const contactId = message.wa_id ? String(message.wa_id) : activeContactRef.current;
      if (contactId && String(activeContactRef.current) === String(contactId)) {
        updateMessagesForContact(contactId, (prev) => applyMessageStatus(prev, message));
      }

      if (message.wa_id) {
        batchedSetChatList((prev) =>
          prev.map((chat) => {
            if (String(chat.number) !== String(message.wa_id)) {
              return chat;
            }
            if (chat.lastMessage?.id !== message.id) {
              return chat;
            }
            return {
              ...chat,
              lastMessage: {
                ...chat.lastMessage,
                status: message.status,
              },
            };
          })
        );
      }
    },
    [updateMessagesForContact, batchedSetChatList]
  );

  const handleChatAssignmentUpdated = useCallback(
    (evt) => {
      const payload = parseEvent(evt);
      if (!payload?.wa_id) {
        return;
      }

      const contactId = String(payload.wa_id);
      const isOpen = String(activeContactRef.current) === contactId;
      const customerName = payload.customer_name || 'Customer';
      const assignedAgentId = payload.agent_id ?? payload.active_agent?.agent_id ?? null;
      const assignedToMe = UserData?.id
        && assignedAgentId != null
        && Number(assignedAgentId) === Number(UserData.id);

      if (assignedToMe) {
        notification.open({
          message: 'Chat assigned to you',
          description: customerName,
          placement: 'topRight',
        });
        fetchInitialChatsRef.current?.();
      } else if (isSupportAgentRef.current) {
        setChatList((prev) => prev.filter((chat) => String(chat.number) !== contactId));
      }

      if (isOpen && payload.system_message) {
        updateMessagesForContact(contactId, (prev) =>
          appendUniqueMessage(prev, normalizeConversationMessage(payload.system_message))
        );
      }
    },
    [UserData?.id, updateMessagesForContact]
  );

  useEffect(() => {
    if (!broadcastUserId) {
      return undefined;
    }

    subscribeUserChat(broadcastUserId, {
      onMenuUpdated: handleMenuUpdated,
      onIncomingMessage: handleIncomingMessage,
      onMessageMeta: handleMessageMeta,
      onMessageStatus: handleMessageStatus,
      onAssignmentUpdated: handleChatAssignmentUpdated,
    });

    return () => leaveUserChat(broadcastUserId);
  }, [broadcastUserId, handleMenuUpdated, handleIncomingMessage, handleMessageMeta, handleMessageStatus, handleChatAssignmentUpdated]);

  useEffect(() => {
    fetchInitialChats();
  }, [fetchInitialChats]);

  return {
    chatList,
    setChatList,
    msgList,
    activeContact,
    setActiveContact,
    loadingChats,
    loadingMoreChats,
    hasMoreChats,
    chatFilter,
    chatSearch,
    crossFilterNew,
    openContactCsw,
    clearCrossFilterNew,
    loadingMessages,
    setLoadingMessages,
    fetchInitialChats,
    fetchMoreChats,
    searchChats,
    fetchMessages,
    markRead,
    appendOptimistic,
    setMessagesForContact,
    updateMessagesForContact,
    chatAccount,
  };
};
